import { useQuery } from "@tanstack/react-query";
import { match, P } from "ts-pattern";
import { API } from "../../../API";
import useTrainingCenterState from "../../../hooks/useTrainingCenterState";
import { isLoading } from "../../../Loading";
import { AgencyId } from "../../../messages";
import { AppState } from "../../../system/AppState";
import { QueryStatus } from "../../../utils/reactQueryUtils";
import { getComplianceBannerDetails } from "../complianceBannerUtils";

interface Props {
  api: API;
  appState: AppState;
  medflytAgencyId: AgencyId | null;
  onComplianceBannerClick: () => void;
}

function useComplianceBannerProps(props: Props) {
  const trainingCenterState = useTrainingCenterState(props.appState);
  const complianceBannerQueryKey = ["compliance_items", props.medflytAgencyId];
  const query = useQuery({
    enabled: props.medflytAgencyId !== null,
    queryKey: complianceBannerQueryKey,
    queryFn: () =>
      props.medflytAgencyId === null
        ? undefined
        : props.api.getComplianceItems(props.medflytAgencyId),
  });

  const bannerProps = match(query)
    .with(P.union(QueryStatus.Loading, QueryStatus.Idle, QueryStatus.Error), () => null)
    .with(QueryStatus.ResolvedAs("data"), (result) => {
      const complianceItems = result.data.items;

      const { title, message, multipleComplianceItems, complianceStatus } =
        getComplianceBannerDetails(
          complianceItems,
          isLoading(trainingCenterState) ? [] : trainingCenterState.bundles.bundles.toArray()
        );
      if (message === null) {
        return null;
      }

      return {
        title,
        message,
        multipleComplianceItems,
        complianceStatus,
        onClick: props.onComplianceBannerClick,
      };
    })
    .run();

  return bannerProps;
}

export default useComplianceBannerProps;
